/**
 * Firebase web SDK singleton — Google sign-in + ID token retrieval.
 *
 * The admin panel auth flow is intentionally simple:
 *   1. User clicks "Sign in with Google" → `signInWithPopup`.
 *   2. We pull a fresh ID token from `currentUser.getIdToken()`.
 *   3. Token is attached to `/v1/admin/*` requests by the axios
 *      interceptor in `lib/api.ts`.
 *   4. The backend's `RequireRole("moderator")` middleware checks the
 *      `role` custom claim and 403s anyone else.
 *
 * We don't keep a Zustand store like the mobile app does; the admin
 * panel only needs `auth.currentUser` directly, and React's
 * `onAuthStateChanged` hook is enough state for our two pages.
 */
import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signOut as fbSignOut,
  type Auth,
} from 'firebase/auth';

function readConfig(): FirebaseOptions {
  // Vite inlines `import.meta.env.VITE_*` at build time; missing values
  // will surface as undefined at runtime and Firebase will throw a
  // useful error from `initializeApp`. We don't pre-validate because
  // the only reason a value would be missing is a misconfigured CI
  // build, which is rare enough to handle via the Firebase error.
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

let appSingleton: FirebaseApp | null = null;
let authSingleton: Auth | null = null;

export function getFirebaseAuth(): Auth {
  if (authSingleton !== null) return authSingleton;
  appSingleton = initializeApp(readConfig());
  authSingleton = getAuth(appSingleton);
  return authSingleton;
}

/**
 * Open the Google sign-in popup. Resolves to the freshly-signed-in
 * user's ID token. Caller is responsible for catching popup-closed /
 * blocked errors and surfacing them in the UI.
 */
export async function signInWithGoogle(): Promise<string> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  const credential = await signInWithPopup(auth, provider);
  return credential.user.getIdToken();
}

/** Sign out of Firebase. Idempotent. */
export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await fbSignOut(auth);
}

/**
 * Return a fresh ID token for the current user, or null if no user is
 * signed in. `forceRefresh=true` bypasses the 1h Firebase token cache —
 * used when the API returns 401 with an "expired" hint.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user === null) return null;
  return user.getIdToken(forceRefresh);
}
